import ReactDomServer from 'react-dom/server';
import { Example } from './model';
import * as KitchenComponents from '@inmoment/the-kitchen';
import * as React from 'react';
import { CodeBlock } from '../../cookbook-ui/custom-components/CodeBlock/CodeBlock';
import { pascalToKebab } from '../../pages/automated-pages-utils/code-snippet-utils/codeFormattingFns';
import { compareVersions, kitchenVersion } from '../../utils/version';

interface ExampleViewProps {
  examples: Example[];
  componentName: string;
}
interface ExampleItemProps {
  example: Example;
  componentName: string;
}

const isExampleReleased = (e: Example): boolean => !e.releaseVersion || compareVersions(e.releaseVersion, kitchenVersion) !== 1;

export const ExampleView = React.memo((props: ExampleViewProps) => {
  const { examples: allExamples, componentName } = props;
  const examples = allExamples.filter(isExampleReleased);

  return examples.length > 0 ? (
    <>
      <div>
        <h2>Examples</h2>
        <br />
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {examples.map((example) => (
            <ExampleItem key={example.name} example={example} componentName={componentName}></ExampleItem>
          ))}
        </div>
      </div>
    </>
  ) : null;
});

const stringifyPropValue = (propValue: unknown): string => {
  return JSON.stringify(propValue, (_key, value) => (React.isValidElement(value) ? ReactDomServer.renderToStaticMarkup(value) : value));
};

const generateReactExample = (example: Example, componentName: string) => {
  let code = '';
  code += `<${componentName}\n`;

  const props = typeof example.props === 'function' ? example.props('react') : example.props;

  const propKeys = Object.keys(props);
  propKeys.forEach((prop) => {
    const propValue = props[prop];

    code += `    ${prop}={`;
    switch (typeof propValue) {
      case 'number':
      case 'boolean':
        code += `${propValue}`;
        break;
      case 'string':
        code += `"${propValue}"`;
        break;
      default:
        code += stringifyPropValue(propValue);
        break;
    }
    code += `}\n`;
  });
  code += `></${componentName}>`;
  return code;
};

const generateAngularExample = (example: Example, componentName: string) => {
  let code = '';
  code += `<${pascalToKebab(componentName)}\n`;

  const props = typeof example.props === 'function' ? example.props('angular') : example.props;

  const propKeys = Object.keys(props);
  propKeys.forEach((prop) => {
    const propValue = props[prop];

    // left-hand side
    switch (typeof propValue) {
      case 'string':
        code += `    ${prop}="`;
        break;
      default:
        code += `    [${prop}]="`;
        break;
    }

    // right-hand side
    switch (typeof propValue) {
      case 'number':
      case 'boolean':
        code += `${propValue}`;
        break;
      case 'string':
        code += `${propValue}`;
        break;
      default:
        code += stringifyPropValue(propValue).replaceAll('"', "'");
        break;
    }

    // final quote
    code += `"\n`;
  });
  code += `></${pascalToKebab(componentName)}>`;

  return code;
};

export const ExampleItem = (props: ExampleItemProps) => {
  const { example, componentName } = props;
  const [codeExpanded, setCodeExpanded] = React.useState(false);

  if (!isExampleReleased(example)) {
    return null;
  }

  const exampleProps = typeof example.props === 'function' ? example.props('react') : example.props;

  return (
    <div style={{ width: 'fit-content', display: 'flex', flexDirection: 'column' }}>
      <KitchenComponents.BasicTile
        title={example.name}
        subtitle={example.description}
        hasTopBar={false}
        titleSeparator={true}
        isInteractive={false}
        stretchSlot={true}
      >
        <KitchenComponents.DefaultButton
          text={`${codeExpanded ? 'Hide' : 'Show'} Code`}
          fill={'text'}
          iconRight="add_circle"
          buttonSize={'small'}
          onClick={() => setCodeExpanded(!codeExpanded)}
          slot="action"
          style={{ marginLeft: 40 }}
        ></KitchenComponents.DefaultButton>

        <slot slot="children">
          <div style={{ width: '100%', padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', ...example.slotStyle }}>
            {React.createElement((KitchenComponents as any)[componentName], exampleProps)}
            {codeExpanded ? (
              <div style={{ width: '100%', marginTop: 30 }}>
                <div>
                  React:
                  <CodeBlock code={example.react ?? generateReactExample(example, componentName)} monospace></CodeBlock>
                </div>
                <div>
                  Angular:
                  <CodeBlock code={example.angular ?? generateAngularExample(example, componentName)} monospace></CodeBlock>
                </div>
              </div>
            ) : null}
          </div>
        </slot>
      </KitchenComponents.BasicTile>
    </div>
  );
};
